import React, {useEffect} from 'react'
import {Outlet, useOutletContext, useParams} from 'react-router-dom'
import Loader from '../common/Loader'
import AlertMessage from '../common/AlertMessage'
import {
  useGetAccountsQuery,
  useGetReservationQuery
} from '../../services/residentPortalApi'
import {Account, AccountContext} from '../../types/app-types'
import {openWebSocket} from '../../services/residentPortalWS'
import {useAppDispatch} from '../../store/hooks'
import AppLayout from './AppLayout'

interface AccountsQueryResult {
  [key: string]: any
  data?: Account[]
  isLoading: boolean
}

function selectCurrentAccount(result: AccountsQueryResult, accountId: string) {
  const accounts = result.data || []
  let currentAccount = null
  for (const account of accounts) {
    if (account.reservationId === accountId) {
      currentAccount = account
      break
    }
  }
  return {
    ...result,
    currentAccount
  }
}

const AccountLayout = () => {
  const {id} = useParams()
  const dispatch = useAppDispatch()
  const accountsQuery = useGetAccountsQuery(undefined, {
    selectFromResult: (result: AccountsQueryResult) =>
      selectCurrentAccount(result, id)
  })
  const reservationQuery = useGetReservationQuery(id, {skip: !id})
  const reservation = reservationQuery.data

  let accountContent = null
  if (reservationQuery.isLoading || accountsQuery.isLoading) {
    accountContent = <Loader />
  } else if (reservation) {
    const unitName = reservation.unit
      ? `Unit ${reservation.unit?.name}`
      : `${reservation.unitGroup?.name}`
    const accountName = `${reservation.property?.name} - ${unitName}`
    const accountContext: AccountContext = {
      account: accountsQuery.currentAccount,
      accountId: id,
      accountName,
      reservation
    }
    accountContent = (
      <>
        <h1 className="page-header">{accountName}</h1>
        <Outlet context={accountContext} />
      </>
    )
  } else {
    const message =
      'Error occurred while loading your account.  ' +
      'If the problem persists, please contact support: hello@placemakr.com'
    accountContent = <AlertMessage type="error" message={message} />
  }

  // initialize a websocket for API notifications
  useEffect(() => {
    // open a websocket to receive API notifications
    const webSocketPromise: Promise<WebSocket> = openWebSocket(dispatch)

    // cleanup function - closes websocket connection
    return () => {
      webSocketPromise
        .then((ws: WebSocket) => ws.close())
        .catch((e: any) => console.log('Failed to close websocket', e))
    }
  }, [])

  return <AppLayout showAccountLinks={true}>{accountContent}</AppLayout>
}

export default AccountLayout

export const useAccountContext = () => useOutletContext<AccountContext>()
